// AudioPlayer — a styled audio player matching the parchment/ink design system.
// Replaces the raw <audio controls> element. Play/pause, seek, time readout,
// playback speed, and download — driven by a hidden <audio> element.

import { useEffect, useRef, useState } from 'react';

interface AudioPlayerProps {
  src: string;
  downloadName?: string;
}

const SPEEDS = [1, 1.25, 1.5, 2, 0.75] as const;

function fmt(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export function AudioPlayer({ src, downloadName }: AudioPlayerProps) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const [speedIdx, setSpeedIdx] = useState(0);
  const [ready, setReady] = useState(false);

  // Reset when the source changes (e.g. a new render).
  useEffect(() => {
    setPlaying(false);
    setCurrent(0);
    setDuration(0);
    setReady(false);
    setSpeedIdx(0);
  }, [src]);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) {
      void a.play();
    } else {
      a.pause();
    }
  };

  const onSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const a = audioRef.current;
    if (!a) return;
    const t = Number(e.target.value);
    a.currentTime = t;
    setCurrent(t);
  };

  const cycleSpeed = () => {
    const next = (speedIdx + 1) % SPEEDS.length;
    setSpeedIdx(next);
    if (audioRef.current) audioRef.current.playbackRate = SPEEDS[next]!;
  };

  const pct = duration > 0 ? (current / duration) * 100 : 0;

  return (
    <div className="rounded-lg border border-soft bg-white/50 p-3">
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onLoadedMetadata={(e) => {
          setDuration(e.currentTarget.duration);
          setReady(true);
        }}
        onTimeUpdate={(e) => setCurrent(e.currentTarget.currentTime)}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
      />

      <div className="flex items-center gap-3">
        <button
          onClick={toggle}
          disabled={!ready}
          aria-label={playing ? 'Pause' : 'Play'}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-ink text-parchment transition-transform hover:scale-105 active:scale-95 disabled:opacity-40"
        >
          {playing ? (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
              <rect x="3.5" y="2.5" width="3.2" height="11" rx="1" />
              <rect x="9.3" y="2.5" width="3.2" height="11" rx="1" />
            </svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
              <path d="M4.5 2.8v10.4a.6.6 0 0 0 .92.5l8.2-5.2a.6.6 0 0 0 0-1l-8.2-5.2a.6.6 0 0 0-.92.5z" />
            </svg>
          )}
        </button>

        <div className="min-w-0 flex-1">
          <input
            type="range"
            className="range-accent"
            min={0}
            max={duration || 0}
            step={0.1}
            value={current}
            onChange={onSeek}
            disabled={!ready}
            aria-label="Seek"
            style={{
              background: `linear-gradient(to right, #7a4b34 ${pct}%, #efeae0 ${pct}%)`,
            }}
          />
          <div className="mt-1 flex items-center justify-between text-[11px] tabular-nums text-muted">
            <span>{fmt(current)}</span>
            <span>{ready ? fmt(duration) : '—:—'}</span>
          </div>
        </div>

        <button
          onClick={cycleSpeed}
          className="shrink-0 rounded-md border border-soft px-2 py-1 text-xs font-medium text-muted transition-colors hover:border-accent/40 hover:text-ink"
          aria-label="Playback speed"
          title="Playback speed"
        >
          {SPEEDS[speedIdx]}×
        </button>

        <a
          href={src}
          download={downloadName}
          className="shrink-0 rounded-md p-2 text-muted transition-colors hover:bg-soft/60 hover:text-ink"
          aria-label="Download mp3"
          title="Download .mp3"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M8 2v8M8 10L5 7M8 10l3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M3 12.5h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </a>
      </div>
    </div>
  );
}
